import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_DROP_FILES, fromBase64Url, openDrop, sealDrop, sealedDropDataBytes, toBase64Url, type DropSession } from '@sss/crypto';
import { CHALLENGE_ACTIONS, DROP_LIMITS } from '@sss/types';
import FlameMark from '../Brand/FlameMark';
import CopyButton from '../Secret/CopyButton';
import HumanCheck from '../Secret/HumanCheck';
import RelayPassField from '../Secret/RelayPassField';
import { LinkStep, PassphraseStep } from '../Secret/ShareSteps';
import secretStyles from '../Secret/Secret.module.css';
import { useHumanCheck } from '../../hooks/useHumanCheck';
import { usePass } from '../../hooks/usePass';
import { completeDrop, createDrop, deleteDrop, dropProblem, getDropConfig, putDropPart } from '../../lib/dropsApi';
import { forgetDropResume, loadDropResume, sameFiles, saveDropResume, type DropResume } from '../../lib/dropResume';
import { dropHold, uploadParts } from '../../lib/dropUpload';
import { buildDropLink, buildDropStatusLink } from '../../lib/secretLinks';
import { fitFileName, formatBytes } from '../../lib/tools';
import ui from '../../styles/ui.module.css';
import createStyles from '../../pages/CreatePage.module.css';
import liveStyles from '../../pages/LivePage.module.css';
import styles from './DropUploadPanel.module.css';

/**
 * "Leave it for later" for files (docs/adr/0015): seal the files here, hold
 * the time chosen from the pass, upload them in parts, then hand over the
 * link. An interrupted upload resumes from the record kept in IndexedDB,
 * with the same files picked again. The plaintext and the link key never
 * leave this tab; the server gets sealed records only.
 */

const DAY_SECONDS = 24 * 60 * 60;

/** "about 33 min", "about 2 h 10 min", "under a minute". */
const formatDuration = (seconds: number, t: (key: string, options?: Record<string, unknown>) => string): string => {
  if (seconds < 60) return t('drop.uploading.underMinute');
  const minutes = Math.round(seconds / 60);
  return minutes < 60 ? t('drop.uploading.minutes', { count: minutes }) : t('drop.uploading.hours', { hours: Math.floor(minutes / 60), minutes: minutes % 60 });
};
const QUICK_DAYS = [1, 7, 30] as const;

interface Uploading {
  id: string;
  parts: number;
  done: number;
  totalBytes: number;
  sentBytes: number;
  /** Bytes a second, measured since this run started; null until there's enough to go on. */
  speed: number | null;
  retryIn: number | null;
}

interface Finished {
  link: string;
  passphrase: string;
  endsAt: string;
  maxDownloads: number;
  id: string;
  senderToken: string;
}

export default function DropUploadPanel() {
  const { t, i18n } = useTranslation();
  const [maxBytes, setMaxBytes] = useState<number | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [days, setDays] = useState(7);
  const [downloads, setDownloads] = useState(1);
  const [passphrase, setPassphrase] = useState('');
  const [pass, changePass] = usePass();
  const humanCheck = useHumanCheck(CHALLENGE_ACTIONS.createDrop);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState<Uploading | null>(null);
  const [finished, setFinished] = useState<Finished | null>(null);
  const [resume, setResume] = useState<DropResume | null>(null);
  const [resumePassphrase, setResumePassphrase] = useState('');
  const controller = useRef<AbortController | null>(null);
  // The upload in this tab: what a retry or a cancel needs, without picking the files again.
  const active = useRef<{ record: DropResume; session: DropSession; files: File[]; passphrase: string } | null>(null);
  const [canRetry, setCanRetry] = useState(false);
  const wakeLock = useRef<WakeLockSentinel | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);

  const filesTotal = files.reduce((sum, f) => sum + f.size, 0);
  const records = Math.ceil(filesTotal / 2 ** 20);
  // The manifest is at least 4 KiB sealed; close enough for an estimate before sealing.
  const holdEstimate = files.length > 0 ? dropHold(records, 64, 4124, days * DAY_SECONDS) : 0;

  useEffect(() => {
    let cancelled = false;
    getDropConfig()
      .then((c) => !cancelled && setMaxBytes(c.maxBytes))
      .catch(() => !cancelled && setMaxBytes(0));
    void loadDropResume().then((r) => !cancelled && setResume(r));
    return () => {
      cancelled = true;
    };
  }, []);

  const isUploading = uploading !== null;

  // Leaving mid-upload loses nothing (it can resume), but the browser should still ask.
  useEffect(() => {
    if (!isUploading) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    // A sleeping screen pauses the upload; keep it awake while there's work.
    const lock = async () => {
      try {
        wakeLock.current = (await navigator.wakeLock?.request('screen')) ?? null;
      } catch {
        // Not allowed (battery saver, an iframe): uploading still works.
      }
    };
    const onVisible = () => document.visibilityState === 'visible' && void lock();
    void lock();
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', onVisible);
      void wakeLock.current?.release().catch(() => undefined);
      wakeLock.current = null;
    };
  }, [isUploading]);

  useEffect(() => () => controller.current?.abort(new Error('left')), []);

  useEffect(() => {
    if (finished) heading.current?.focus();
  }, [finished]);

  /** Upload the parts not done, then complete; the resume record follows along. */
  async function run(record: DropResume, session: DropSession, chosen: File[], keptPassphrase: string) {
    const done = new Set(record.partsDone);
    const abort = new AbortController();
    controller.current = abort;
    active.current = { record, session, files: chosen, passphrase: keptPassphrase };
    setCanRetry(false);
    setProblem(null);
    setUploading({ id: record.id, parts: record.parts, done: done.size, totalBytes: sealedDropDataBytes(session.totalRecords), sentBytes: 0, speed: null, retryIn: null });
    let startBytes: number | null = null;
    const started = performance.now();
    try {
      await uploadParts({
        files: chosen,
        session,
        parts: record.parts,
        partSize: record.partSize,
        done,
        put: (part, body, signal, onProgress) => putDropPart(record.id, part, body, record.uploadToken, signal, onProgress),
        onBytes: (sent) => {
          startBytes ??= sent;
          const seconds = (performance.now() - started) / 1000;
          const speed = seconds >= 3 && sent > startBytes ? (sent - startBytes) / seconds : null;
          setUploading((u) => u && { ...u, sentBytes: sent, speed });
        },
        onPart: () => {
          record.partsDone = [...done];
          void saveDropResume({ ...record });
          setUploading((u) => u && { ...u, done: done.size });
        },
        onRetry: (seconds) => setUploading((u) => u && { ...u, retryIn: seconds }),
        signal: abort.signal,
        // Two at a time: on a slow link a third only delays the first part's progress.
        concurrency: 2,
      });
      const { endsAt } = await completeDrop(record.id, record.uploadToken);
      await forgetDropResume();
      active.current = null;
      setResume(null);
      setFiles([]);
      setPassphrase('');
      setFinished({
        link: buildDropLink(window.location.origin, record.id, record.linkKey),
        passphrase: keptPassphrase,
        endsAt,
        maxDownloads: record.maxDownloads,
        id: record.id,
        senderToken: record.senderToken,
      });
    } catch (err) {
      if (abort.signal.aborted) return;
      setProblem(t(`drop.problems.${dropProblem(err)}`));
      setCanRetry(true);
    } finally {
      setUploading(null);
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (files.length === 0) return setProblem(t('drop.errors.noFiles'));
    if (files.length > MAX_DROP_FILES) return setProblem(t('drop.errors.tooManyFiles', { max: MAX_DROP_FILES }));
    if (maxBytes !== null && sealedDropDataBytes(records) > maxBytes) return setProblem(t('drop.errors.tooLarge', { max: formatBytes(maxBytes) }));
    if (!Number.isInteger(days) || days < 1) return setProblem(t('drop.errors.days'));
    if (!pass) return setProblem(t('drop.errors.noPass'));
    const turnstileToken = humanCheck.take();
    if (!turnstileToken) {
      return setProblem(t(humanCheck.state === 'failed' || humanCheck.state === 'unavailable' ? 'live.errors.checkFailed' : 'create.errors.checkPending'));
    }
    setProblem(null);
    setBusy(true);
    try {
      const manifest = { files: files.map((f) => ({ name: fitFileName(f.name), size: f.size, type: f.type.slice(0, 255) })) };
      const sealed = await sealDrop(manifest, { passphrase: passphrase || undefined });
      const keepSeconds = Math.min(days * DAY_SECONDS, DROP_LIMITS.maxKeepSeconds);
      const drop = await createDrop({
        header: toBase64Url(sealed.header),
        sealedManifest: toBase64Url(sealed.sealedManifest),
        keepSeconds,
        maxDownloads: downloads,
        pass,
        turnstileToken,
      });
      const record: DropResume = {
        id: drop.id,
        uploadToken: drop.uploadToken,
        senderToken: drop.senderToken,
        linkKey: toBase64Url(sealed.linkKey),
        header: toBase64Url(sealed.header),
        sealedManifest: toBase64Url(sealed.sealedManifest),
        hasPassphrase: passphrase.length > 0,
        files: files.map((f) => ({ name: f.name, size: f.size, lastModified: f.lastModified })),
        parts: drop.parts,
        partSize: drop.partRecords,
        partsDone: [],
        keepSeconds,
        maxDownloads: downloads,
        uploadDeadline: drop.uploadDeadline,
      };
      await saveDropResume(record);
      setBusy(false);
      await run(record, sealed.session, files, passphrase);
    } catch (err) {
      setProblem(t(`drop.problems.${dropProblem(err)}`));
    } finally {
      setBusy(false);
    }
  }

  async function onResume(chosen: File[]) {
    if (!resume) return;
    if (!sameFiles(resume.files, chosen)) return setProblem(t('drop.resume.different'));
    if (resume.hasPassphrase && !resumePassphrase) return setProblem(t('drop.resume.needPassphrase'));
    setProblem(null);
    try {
      const { session } = await openDrop({
        header: fromBase64Url(resume.header),
        sealedManifest: fromBase64Url(resume.sealedManifest),
        linkKey: fromBase64Url(resume.linkKey),
        passphrase: resume.hasPassphrase ? resumePassphrase : undefined,
      });
      await run(resume, session, chosen, resume.hasPassphrase ? resumePassphrase : '');
    } catch {
      setProblem(t('drop.resume.wrongPassphrase'));
    }
  }

  /** Stop the upload and delete the drop; the hold goes back to the pass, less 1%. */
  async function cancel() {
    const record = active.current?.record ?? resume;
    controller.current?.abort(new Error('cancelled'));
    if (record) await deleteDrop(record.id, record.uploadToken).catch(() => undefined);
    await forgetDropResume();
    active.current = null;
    setResume(null);
    setUploading(null);
    setCanRetry(false);
    setProblem(null);
  }

  function retry() {
    const current = active.current;
    if (current) void run(current.record, current.session, current.files, current.passphrase);
  }

  if (finished) {
    const hasPassphrase = finished.passphrase.length > 0;
    const statusLink = buildDropStatusLink(window.location.origin, finished.id, finished.senderToken);
    const endsAt = new Date(finished.endsAt).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });
    return (
      <section className={secretStyles.created} aria-labelledby="drop-created-title">
        <header className={secretStyles.createdHeader}>
          <h2 id="drop-created-title" className={secretStyles.createdTitle} tabIndex={-1} ref={heading}>
            {t('drop.done.title')}
          </h2>
          <p className={ui.hint}>{t('drop.done.kept', { when: endsAt, count: finished.maxDownloads })}</p>
        </header>
        <LinkStep link={finished.link} step={hasPassphrase ? 1 : null} text={t(hasPassphrase ? 'drop.share.linkTextPassphrase' : 'drop.share.linkText')} />
        {hasPassphrase && (
          <>
            <p className={secretStyles.between} aria-hidden="true">
              <span>{t('secret.created.between')}</span>
            </p>
            <PassphraseStep passphrase={finished.passphrase} />
          </>
        )}
        <section className={secretStyles.privateStrip} aria-labelledby="drop-status-title">
          <div className={secretStyles.privateText}>
            <h3 id="drop-status-title" className={secretStyles.privateTitle}>
              {t('drop.done.statusTitle')}
            </h3>
            <p className={ui.hint}>{t('drop.done.statusHint')}</p>
          </div>
          <a className={secretStyles.stripButton} href={statusLink} target="_blank" rel="noopener noreferrer">
            {t('drop.done.openStatus')}
          </a>
          <CopyButton value={statusLink} label={t('secret.copy')} accessibleLabel={t('drop.done.copyStatus')} className={secretStyles.stripButton} />
        </section>
        <div className={styles.doneActions}>
          <button
            type="button"
            className={ui.buttonSecondary}
            onClick={() => void deleteDrop(finished.id, finished.senderToken).then(() => setFinished(null), () => setProblem(t('drop.problems.other')))}
          >
            {t('drop.done.delete')}
          </button>
        </div>
      </section>
    );
  }

  if (uploading) {
    const percent = uploading.totalBytes > 0 ? Math.floor((uploading.sentBytes / uploading.totalBytes) * 100) : 100;
    const left = uploading.speed ? Math.max(0, uploading.totalBytes - uploading.sentBytes) / uploading.speed : null;
    return (
      <section className={`${ui.panel} ${createStyles.form}`} aria-labelledby="drop-uploading-title">
        <h2 id="drop-uploading-title" className={createStyles.formTitle}>
          {t('drop.uploading.title')}
        </h2>
        <p className={secretStyles.notice}>{t('drop.uploading.keepOpen')}</p>
        <progress className={liveStyles.progress} value={uploading.sentBytes} max={Math.max(uploading.totalBytes, 1)} aria-label={t('drop.uploading.progress')} />
        <p role="status" className={ui.hint} data-testid="drop-progress">
          {uploading.retryIn !== null
            ? t('drop.uploading.retrying', { seconds: uploading.retryIn, percent })
            : t('drop.uploading.line', { percent, sent: formatBytes(uploading.sentBytes), total: formatBytes(uploading.totalBytes) })}
          {uploading.retryIn === null && uploading.speed !== null && left !== null && (
            <> · {t('drop.uploading.rate', { speed: formatBytes(uploading.speed), left: formatDuration(left, t) })}</>
          )}
        </p>
        <button type="button" className={ui.buttonSecondary} onClick={() => void cancel()}>
          {t('drop.uploading.cancel')}
        </button>
      </section>
    );
  }

  if (maxBytes === 0) {
    return (
      <section className={`${ui.panel} ${createStyles.form}`}>
        <p>{t('drop.unavailable')}</p>
      </section>
    );
  }

  return (
    <form className={`${ui.panel} ${createStyles.form}`} onSubmit={onSubmit} noValidate aria-labelledby="drop-form-title" aria-busy={busy}>
      <div className={createStyles.formHeader}>
        <h2 id="drop-form-title" className={createStyles.formTitle}>
          {t('drop.form.title')}
        </h2>
        <span className={ui.badge}>{t('drop.form.badge')}</span>
      </div>

      {resume && (
        <section className={styles.resume} aria-labelledby="drop-resume-title">
          <h3 id="drop-resume-title" className={styles.resumeTitle}>
            {t('drop.resume.title')}
          </h3>
          <p className={ui.hint}>
            {t('drop.resume.body', { count: resume.files.length, percent: Math.floor((resume.partsDone.length / Math.max(resume.parts, 1)) * 100) })}
          </p>
          {resume.hasPassphrase && (
            <label className={ui.field}>
              {t('create.form.passphraseLabel')}
              <input type="password" value={resumePassphrase} onChange={(e) => setResumePassphrase(e.target.value)} autoComplete="off" />
            </label>
          )}
          <label className={ui.field}>
            {t('drop.resume.pick')}
            <input type="file" multiple className={liveStyles.file} onChange={(e) => void onResume([...(e.target.files ?? [])])} />
          </label>
          <button type="button" className={ui.buttonSecondary} onClick={() => void cancel()}>
            {t('drop.resume.discard')}
          </button>
        </section>
      )}

      <label className={ui.field}>
        {t('drop.form.filesLabel')}
        <input type="file" multiple className={liveStyles.file} onChange={(e) => setFiles([...(e.target.files ?? [])])} aria-describedby="drop-files-hint" />
        <span id="drop-files-hint" className={ui.hint}>
          {files.length > 0
            ? t('live.form.filesChosen', { count: files.length, size: formatBytes(filesTotal) })
            : t('drop.form.filesHint', { max: formatBytes(maxBytes ?? 0), files: MAX_DROP_FILES })}
        </span>
      </label>

      <fieldset className={styles.keep}>
        <legend className={styles.legend}>{t('drop.form.keepLegend')}</legend>
        <div className={styles.keepRow}>
          <label className={styles.daysField}>
            <input type="number" min={1} step={1} value={days} onChange={(e) => setDays(Math.floor(Number(e.target.value)))} aria-describedby="drop-keep-hint" />
            <span>{t('drop.form.days', { count: days })}</span>
          </label>
          {QUICK_DAYS.map((d) => (
            <button key={d} type="button" className={days === d ? styles.quickOn : styles.quick} aria-pressed={days === d} onClick={() => setDays(d)}>
              {t('drop.form.quick', { count: d })}
            </button>
          ))}
        </div>
        <span id="drop-keep-hint" className={ui.hint}>
          {t('drop.form.keepHint')}
        </span>
      </fieldset>

      <div className={createStyles.row}>
        <label className={ui.field}>
          {t('drop.form.downloadsLabel')}
          <select value={downloads} onChange={(e) => setDownloads(Number(e.target.value))}>
            {Array.from({ length: DROP_LIMITS.maxDownloads }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {t('drop.form.downloads', { count: n })}
              </option>
            ))}
          </select>
        </label>
        <label className={ui.field}>
          {t('create.form.passphraseLabel')}
          <input type="password" value={passphrase} onChange={(e) => setPassphrase(e.target.value)} placeholder={t('create.form.passphrasePlaceholder')} autoComplete="new-password" />
          <span className={ui.hint}>{t('create.form.passphraseHint')}</span>
        </label>
      </div>

      <RelayPassField pass={pass} onChange={changePass} needed={holdEstimate} hintKey="drop.form.passHint" notEnoughKey="drop.form.notEnough" haveKey="drop.form.have" />
      {files.length > 0 && (
        <p className={ui.hint} data-testid="drop-hold">
          {t('drop.form.hold', { hold: formatBytes(holdEstimate), days })}
        </p>
      )}

      <HumanCheck check={humanCheck} />

      {problem && (
        <p role="alert" className={ui.error}>
          {problem}
        </p>
      )}
      {canRetry && (
        <div className={styles.doneActions}>
          <button type="button" className={ui.button} onClick={retry}>
            {t('drop.uploading.retry')}
          </button>
          <button type="button" className={ui.buttonSecondary} onClick={() => void cancel()}>
            {t('drop.uploading.cancel')}
          </button>
        </div>
      )}

      <button type="submit" className={ui.button} disabled={busy || canRetry}>
        <FlameMark size={18} tone="onAccent" />
        {busy ? t('drop.form.sealing') : t('drop.form.submit')}
      </button>
      <p className={`${ui.hint} ${createStyles.terms}`}>{t('drop.form.terms')}</p>
    </form>
  );
}
